<template>
  <!-- Kontener widoku aktywnych filtrów dla danych pojazdu -->
  <!-- Widoczny tylko, gdy co najmniej jeden filtr został wybrany -->
  <div v-if="isChoosen()" class="filter d-flex flex-column">
    <!-- Nagłówek sekcji z tytułem i ikoną usuwania filtrów -->
    <h5 class="margin-0">
      {{ t('vehicle-data') }}
      <!-- Ikona kosza: usuwa wszystkie filtry dla danych pojazdu -->
      <span class="icon-trash" @click="removeFilter()" role="button"></span>
    </h5>

    <!-- Iteracja po przefiltrowanych danych pojazdu -->
    <div
      v-for="(label, key) in filteredVehicleData"
      :key="key"
      class="d-flex info"
    >
      <!-- Jeżeli pole jest "single" (np. plateLpr, similarity) -->
      <span v-if="isSingle(key)">
        <single-filter-view
          :name="label" 
          :field-name="key"
          :value="filtersStore.filters[key]"
        />
      </span>
      <!-- W przeciwnym razie używamy widoku zakresowego -->
      <span v-else>
        <range-filter-view
          :name="label"
          :type-name="key"
        />
      </span>
    </div>

  </div>
</template>

<script>
import { useArchivalFiltersStore } from "../../stores/archivalFilters"; // Store z filtrami archiwalnymi
import RangeFilterView from "./RangeFilterView.vue";                   // Widok zakresowego filtra
import SingleFilterView from "./SingleFilterView.vue";                 // Widok filtra pojedynczego

export default {
  name: "VehicleDataFilterView",
  components: { SingleFilterView, RangeFilterView }, // Rejestracja komponentów potomnych
  
  setup() {
    // Uzyskanie instancji store Pinia
    const filtersStore = useArchivalFiltersStore();
    return { filtersStore };
  },

  computed: {
    /**
     * Generuje obiekt z aktywnymi filtrami danych pojazdu.
     * Łączy pola minX/maxX w pary oraz uwzględnia pojedyncze filtry.
     */
    filteredVehicleData() {
      const filters = this.filtersStore.filters;
      const vehicleData = this.filtersStore.vehicleData; // Mapa {key: label}
      const result = {};
      const handled = new Set(); // Zbiór już przetworzonych kluczy

      // Iteracja po definicjach pól vehicleData
      for (const [key, label] of Object.entries(vehicleData)) {
        if (handled.has(key)) continue; // Pomijamy już przetworzone

        // Sprawdzenie, czy pole ma prefix min/max
        const match = key.match(/^(min|max)(.+)$/);
        if (match) {
          const base = match[2];
          const minKey = `min${base}`;
          const maxKey = `max${base}`;
          // Czy mamy ustawione min lub max w store.filters?
          const hasMin = filters[minKey] !== undefined && filters[minKey] !== "" && filters[minKey] !== null;
          const hasMax = filters[maxKey] !== undefined && filters[maxKey] !== "" && filters[maxKey] !== null;

          // Jeśli któryś z zakresów jest ustawiony, dodajemy do wyniku parę minKey
          if (hasMin || hasMax) {
            result[minKey] = label;
          }
          handled.add(minKey);
          handled.add(maxKey);
        } else {
          // Dla pól pojedynczych (np. plateLpr)
          if (filters[key] !== undefined && filters[key] !== "" && filters[key] !== null) {
            result[key] = label;
          }
          handled.add(key);
        }
      }

      return result;
    },
  },

  methods: {
    /**
     * Sprawdza, czy co najmniej jeden filtr danych pojazdu jest ustawiony.
     */
    isChoosen() {
      const filters = this.filtersStore.filters;
      return Object.keys(this.filtersStore.vehicleData).some((key) => {
        return filters[key] !== undefined;
      });
    },

    /**
     * Określa, czy dany klucz jest filtrem pojedynczym.
     * @param {string} name - nazwa klucza
     * @returns {boolean}
     */
    isSingle(name) {
      switch (name) {
        case "plateLpr":
        case "vehicleMake":
        case "vehicleModel":
        case "similarity":
        case "onBlackList":
        case "recidivismOnly":
            return true;
        default:
          return false;
      }
    },

    /**
     * Usuwa wszystkie filtry związane z danymi pojazdu.
     */
    removeFilter() {
      this.filtersStore.removeItem("vehicleData");
    },
  },
};
</script>

<style scoped>
/* Pełna szerokość kontenera */
.filter {
  width: 100%;
}

/* Nagłówek sekcji z białym tłem i obrazkiem */
.filter h5 {
  margin: 0;
  padding: var(--spacing-md) var(--spacing-lg);
  position: relative;
  background: var(--surface-secondary);
  border-bottom: 1px solid var(--border-default);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--text-primary);
}

/* Styl pojedynczej linii info */
.info {
  font-size: var(--font-size-sm);
  padding: var(--spacing-sm) var(--spacing-lg);
  border-bottom: 1px solid var(--border-default);
}

/* Styl ikony kosza: usuwa filtry */
.icon-trash {
  background: url("../../assets/icons/trash.svg") no-repeat left top;
  width: 15px;
  height: 17px;
  position: absolute;
  cursor: pointer;
  top: 8px;
  right: 10px;
}

/* Styl ikony collapse (jeśli używana) */
.icon-collapse {
  background: url("../../assets/icons/zwin.png") no-repeat left top;
  width: 19px;
  height: 19px;
  position: absolute;
  cursor: pointer;
  right: 20px;
}

/* Zmieniona ikona po rozwinięciu */
.collapsed.icon-collapse {
  background: url("../../assets/icons/rozwin.png") no-repeat left top;
}

/* Dodatkowe ikony kategorii pojazdów */
.icon-2 { background: url("../../assets/icons/kategoria-2.png") no-repeat left top; }
.icon-3 { background: url("../../assets/icons/kategoria-3.png") no-repeat left top; }
.icon-5 { background: url("../../assets/icons/kategoria-5.png") no-repeat left top; }
.icon-7 { background: url("../../assets/icons/kategoria-7.png") no-repeat left top; }
.icon-8 { background: url("../../assets/icons/kategoria-8.png") no-repeat left top; }
.icon-9 { background: url("../../assets/icons/kategoria-9.png") no-repeat left top; }
.icon-10 { background: url("../../assets/icons/kategoria-10.png") no-repeat left top; }
.icon-11 { background: url("../../assets/icons/kategoria-11.png") no-repeat left top; }
</style>
